// E8MapDistancesPFClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 12) - odleglosci na kratce, P/F.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../components/diagrams/SvgMathLabel";
import TruthInput from "../../../components/inputs/TruthInput";

const TRIPLES = [
  { west: 4, north: 3, sw: 5 },
  { west: 12, north: 5, sw: 13 },
  { west: 8, north: 15, sw: 17 },
  { west: 15, north: 8, sw: 17 },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData({ west, north, museumEast, threshold }) {
  const sw2 = west * west + north * north;
  const sm2 = museumEast * museumEast;
  const mw2 = (museumEast + west) ** 2 + north * north;
  return {
    west,
    north,
    museumEast,
    threshold,
    sw2,
    sm2,
    mw2,
    correctPF: [sm2 === sw2 ? "P" : "F", mw2 < threshold * threshold ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8MapDistancesPFClosedTask",
    data: buildData({ west: 4, north: 3, museumEast: 5, threshold: 10 }),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 12 },
    tags: ["e8", "geometria-analityczna", "kratka", "odleglosc", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const triple = pick(TRIPLES);
  const museumEast = Math.random() < 0.5 ? triple.sw : pick([triple.sw - 1, triple.sw + 1, triple.sw + 2]);
  const mw = Math.sqrt((museumEast + triple.west) ** 2 + triple.north ** 2);
  let threshold = Math.max(4, Math.round(mw + pick([-1, 1])));
  if (threshold * threshold === (museumEast + triple.west) ** 2 + triple.north ** 2) threshold += 1;

  return {
    type: "E8MapDistancesPFClosedTask",
    data: buildData({ west: triple.west, north: triple.north, museumEast, threshold }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_12" },
    tags: ["e8", "geometria-analityczna", "kratka", "odleglosc", "prawda_falsz"],
  };
}

function MapDiagram({ data }) {
  const accent = "#ff9f0a";
  const point = "#22d3ee";
  const { west, north, museumEast } = data;

  // Zakres siatki w km (margines 1 kratki dookola punktow)
  const xMin = -(west + 1);
  const xMax = museumEast + 1;
  const yMin = -2;
  const yMax = north + 1;
  const cols = xMax - xMin;
  const rows = yMax - yMin;

  const scale = Math.max(16, Math.min(30, Math.floor(380 / cols), Math.floor(210 / rows)));
  const PADX = 72; // szerszy margines na poziome podpisy (zachod/wschod)
  const PADY = 50; // margines na pionowe podpisy (polnoc/poludnie)
  const gridW = cols * scale;
  const gridH = rows * scale;
  const svgW = gridW + PADX * 2;
  const svgH = gridH + PADY * 2;

  // (x,y) w km -> piksele (os Y skierowana w gore)
  const px = (x) => PADX + (x - xMin) * scale;
  const py = (y) => PADY + (yMax - y) * scale;

  const S = { x: px(0), y: py(0) };
  const Wp = { x: px(-west), y: py(north) };
  const Mp = { x: px(museumEast), y: py(0) };

  const grid = [];
  for (let i = 0; i <= cols; i += 1) {
    const x = PADX + i * scale;
    grid.push(<line key={`v${i}`} x1={x} y1={PADY} x2={x} y2={PADY + gridH} stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />);
  }
  for (let j = 0; j <= rows; j += 1) {
    const y = PADY + j * scale;
    grid.push(<line key={`h${j}`} x1={PADX} y1={y} x2={PADX + gridW} y2={y} stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />);
  }

  const ext = 20; // o ile groty osi wystaja poza siatke
  const topY = PADY - ext;
  const bottomY = PADY + gridH + ext;
  const leftX = PADX - ext;
  const rightX = PADX + gridW + ext;

  // kwadracik skali "1 km" w prawym gornym rogu siatki
  const legX = PADX + gridW - scale;
  const legY = PADY;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0", overflowX: "auto" }}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} role="img" aria-label="Mapa w ukladzie wspolrzednych z punktami W, S i M" style={{ maxWidth: "100%", height: "auto" }}>
        <defs>
          <marker id="mapArrow" markerWidth="9" markerHeight="9" refX="6.5" refY="3" orient="auto">
            <path d="M0,0 L6.5,3 L0,6 Z" fill="currentColor" />
          </marker>
        </defs>
        {grid}

        {/* Osie przez S z grotami w 4 kierunkach */}
        <line x1={S.x} y1={S.y} x2={S.x} y2={topY} stroke="currentColor" strokeWidth="1.5" markerEnd="url(#mapArrow)" />
        <line x1={S.x} y1={S.y} x2={S.x} y2={bottomY} stroke="currentColor" strokeWidth="1.5" markerEnd="url(#mapArrow)" />
        <line x1={S.x} y1={S.y} x2={leftX} y2={S.y} stroke="currentColor" strokeWidth="1.5" markerEnd="url(#mapArrow)" />
        <line x1={S.x} y1={S.y} x2={rightX} y2={S.y} stroke="currentColor" strokeWidth="1.5" markerEnd="url(#mapArrow)" />

        {/* Podpisy kierunkow */}
        <text x={S.x} y={topY - 7} textAnchor="middle" fill="currentColor" fontSize="12">północ</text>
        <text x={S.x} y={bottomY + 16} textAnchor="middle" fill="currentColor" fontSize="12">południe</text>
        <text x={leftX - 6} y={S.y - 7} textAnchor="end" fill="currentColor" fontSize="12">zachód</text>
        <text x={rightX + 6} y={S.y - 7} textAnchor="start" fill="currentColor" fontSize="12">wschód</text>

        {/* Trojkat WSM */}
        <polygon
          points={`${Wp.x},${Wp.y} ${S.x},${S.y} ${Mp.x},${Mp.y}`}
          fill="rgba(255,159,10,0.10)"
          stroke={accent}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />

        {/* Punkty */}
        {[["W", Wp, -13, -13], ["S", S, -14, 15], ["M", Mp, 6, 16]].map(([label, p, dx, dy]) => (
          <g key={label}>
            <circle cx={p.x} cy={p.y} r="4.5" fill={point} />
            <SvgMathLabel x={p.x + dx} y={p.y + dy} math={label} width={20} height={20} fontSize={14} />
          </g>
        ))}

        {/* Skala: 1 kratka = 1 km */}
        <rect x={legX} y={legY} width={scale} height={scale} fill="none" stroke={accent} strokeWidth="2" />
        <text x={legX + scale / 2} y={legY - 5} textAnchor="middle" fill="currentColor" fontSize="11">1 km</text>
        <text x={legX + scale + 5} y={legY + scale / 2 + 4} textAnchor="start" fill="currentColor" fontSize="11">1 km</text>
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Na kartce w kratkę Jurek wykonał rysunek. Punkty <InlineMath math="M" />, <InlineMath math="W" />, <InlineMath math="S" /> oznaczają położenia odpowiednio: muzeum, wieży widokowej oraz schroniska. Wieża widokowa znajduje się <InlineMath math={`${data.north}\\ \\text{km}`} /> na północ i <InlineMath math={`${data.west}\\ \\text{km}`} /> na zachód od schroniska. Muzeum znajduje się <InlineMath math={`${data.museumEast}\\ \\text{km}`} /> na wschód od schroniska.
      </p>
      <MapDiagram data={data} />
      <p>Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.</p>
      <p>1. Odległość w linii prostej schroniska od muzeum jest równa odległości w linii prostej schroniska od wieży widokowej.</p>
      <p>2. Odległość w linii prostej muzeum od wieży widokowej jest mniejsza niż <InlineMath math={`${data.threshold}\\ \\text{km}`} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) => onChange?.({ ...value, [key]: nextValue });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput value={value[key] || null} disabled={disabled} onChange={(nextValue) => setValue(key, nextValue)} />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1];
}

export function renderAnswer({ data }) {
  return <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Obliczamy kwadraty odległości od schroniska.</p>
      <BlockMath math={`SM^2=${data.museumEast}^2=${data.sm2}`} />
      <BlockMath math={`SW^2=${data.west}^2+${data.north}^2=${data.sw2}`} />
      <p>Pierwsze zdanie jest {data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}.</p>
      <p><strong>Krok 2.</strong> Odległość między muzeum i wieżą widokową:</p>
      <BlockMath math={`MW^2=(${data.museumEast}+${data.west})^2+${data.north}^2=${data.mw2}`} />
      <p>Porównujemy z <InlineMath math={`${data.threshold}^2=${data.threshold * data.threshold}`} />. Drugie zdanie jest {data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}.</p>
      <p>Odpowiedź: <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`} />.</p>
    </div>
  );
}
